import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { ProgressBar } from '@core/components/ui';

/**
 * File Upload Progress Component
 *
 * Displays upload progress for multiple files with progress bars.
 *
 * @param {Object} props
 * @param {Set<string>} props.uploadingFiles - Set of file names being uploaded
 * @param {Object} props.uploadProgress - Object mapping file names to progress percentages
 *
 * @example
 * <FileUploadProgress
 *   uploadingFiles={new Set(['file1.pdf', 'file2.jpg'])}
 *   uploadProgress={{ 'file1.pdf': 45, 'file2.jpg': 78 }}
 * />
 */
export function FileUploadProgress({ uploadingFiles, uploadProgress }) {
  const { t } = useTranslation();

  if (uploadingFiles.size === 0) {
    return null;
  }

  return (
    <div className="mb-6">
      <p className="text-sm font-medium text-muted mb-3">
        {t('file.uploading', 'Uploading files...')}
      </p>
      <div className="space-y-3">
        {Array.from(uploadingFiles).map((fileName) => (
          <div key={fileName}>
            <ProgressBar
              value={uploadProgress[fileName] || 0}
              label={fileName}
              showPercentage
              size="md"
              color="blue"
            />
          </div>
        ))}
      </div>
    </div>
  );
}

FileUploadProgress.propTypes = {
  uploadingFiles: PropTypes.instanceOf(Set).isRequired,
  uploadProgress: PropTypes.objectOf(PropTypes.number).isRequired,
};
